import { CheckIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import { PAGE_TYPES } from '@dashboard/enums/page-types';

import { PaymentsChargesList } from '@dashboard/scenes/PaymentsCharges';
import { PaymentsSubscriptionsList } from '@dashboard/scenes/PaymentsSubscriptions';
import { SubmissionsList } from '@dashboard/scenes/Submissions';

import Button from '@dashboard/components/Button';
import CopyToClipboard from '@dashboard/components/CopyToClipboard';
import Link from '@dashboard/components/Link';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import CardContent from '@dashboard/components/CardContent';

import PageEditView from '../components/PageEditView';
import { usePreview } from '@/dashboard/context/PreviewContext';
import { eventsAppUrl } from '@dashboard/services/env';

const PageTest = ({ page }) => {
  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  useEffect(() => {
    setPreview({
      type: 'test-checkout',
    });

    return () => {
      setPreview({
        type: 'checkout',
      });
    };
  }, []);

  return (
    <PageEditView
      tabs={[
        {
          ...(page.type === PAGE_TYPES.CHECKOUT && {
            path: '/payments',
            label: 'Test payments',
            component:
              page.productType === 'subscription' ? (
                <TestSubscriptions page={page} />
              ) : (
                <TestPayments page={page} />
              ),
            description: (
              <>
                Paste the card details below into your checkout to make a test payment.{' '}
                <Link to="/help/articles/how-to-make-test-payments" color="default" blank>
                  Learn more
                </Link>
              </>
            ),
          }),
          ...(page.type === 'form' && {
            path: '/payments',
            label: 'Test submissions',
            component: <TestSubmissions page={page} />,
            description: 'Make a test submission to see how your form works for visitors.',
          }),
          ...(page.type === PAGE_TYPES.EVENT && {
            path: '/payments',
            label: 'Test bookings',
            component: <TestPayments page={page} />,
            description: (
              <>
                Paste the card details below into your checkout to make a test payment.{' '}
                <Link to="/help/articles/how-to-make-test-payments" color="default" blank>
                  Learn more
                </Link>
              </>
            ),
          }),
        },
      ]}
    />
  );
};

const TestCardItem = ({ label, value, isLast }) => {
  const [isCopying, setIsCopying] = useState(false);

  return (
    <div className={clsx(`py-2 px-4 flex`, { 'border-b border-gray-100': !isLast })}>
      <div>
        <small>{label}</small>
        <div className="font-semibold">{value}</div>
      </div>

      <div className="ml-auto my-auto">
        <CopyToClipboard
          text={value}
          onCopy={() => {
            setIsCopying(true);

            window.setTimeout(() => {
              setIsCopying(false);
            }, 1000);
          }}
        >
          <Button appearance="light" disabled={isCopying}>
            {isCopying ? (
              <>
                Copied
                <CheckIcon className="ml-2 h-4 w-4" />
              </>
            ) : (
              <>
                Copy
                <ClipboardIcon className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </CopyToClipboard>
      </div>
    </div>
  );
};

function TestSubmissions({ page }) {
  const [refreshKey, setRefreshKey] = useState(0);

  // listen to postmessage checkout event
  React.useEffect(() => {
    const handleCheckoutEvent = event => {
      if (event.data?.type === 'checkoutpage.event') {
        if (event.data.payload.event === 'submission') {
          setRefreshKey(refreshKey + 1);
        }
      }
    };

    window.addEventListener('message', handleCheckoutEvent);

    return () => {
      window.removeEventListener('message', handleCheckoutEvent);
    };
  }, [refreshKey]);

  return (
    <div className="w-full">
      <h1 className="text-xl font-semibold">Submissions</h1>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header">
          <CardListItemElement size="xl">Customer</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
        </CardListItem>

        <SubmissionsList
          options={{
            hidePageName: true,
            hideStatus: true,
            appearance: 'compact',
          }}
          sort={{ createdAt: -1 }}
          filter={{ livemode: false, page: page._id }}
          refreshKey={refreshKey}
        />
      </CardContent>
    </div>
  );
}

function TestPayments({ page }) {
  const [refreshKey, setRefreshKey] = useState(0);

  const eventAppHref = `${eventsAppUrl}/event/${page._id}/guest-list?livemode=false`;

  // listen to postmessage checkout event
  React.useEffect(() => {
    const handleCheckoutEvent = event => {
      if (event.data?.type === 'checkoutpage.event') {
        if (event.data.payload.event === 'checkout') {
          setRefreshKey(refreshKey + 1);
        }
      }
    };

    window.addEventListener('message', handleCheckoutEvent);

    return () => {
      window.removeEventListener('message', handleCheckoutEvent);
    };
  }, [refreshKey]);

  return (
    <div className="w-full">
      {page.type === PAGE_TYPES.EVENT && (
        <Button appearance="secondary" target="_blank" href={eventAppHref}>
          <CheckIcon className="w-4 h-4 mr-2 align-baseline" />
          Test check in guests
        </Button>
      )}
      <div className="my-4">
        <div className="border border-gray-100 rounded">
          <TestCardItem label="Test card number" value="4242 4242 4242 4242" />

          <TestCardItem label="Expiry date" value="12/28" />

          <TestCardItem label="CVC" value="123" isLast />
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold">Payments</h1>
        </div>
      </div>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header">
          <CardListItemElement size="xl">Customer</CardListItemElement>
          <CardListItemElement size="small">Amount</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
        </CardListItem>

        {page.type === PAGE_TYPES.CHECKOUT && (
          <PaymentsChargesList
            options={{
              hidePaymentLink: true,
              hideStatus: true,
              hidePageType: true,
              appearance: 'compact',
            }}
            sort={{ createdAt: -1 }}
            filter={{ livemode: false, page: page._id }}
            refreshKey={refreshKey}
          />
        )}

        {page.type === PAGE_TYPES.EVENT && (
          <PaymentsChargesList
            options={{
              hidePaymentLink: true,
              hideStatus: true,
              hidePageType: true,
              appearance: 'compact',
            }}
            sort={{ createdAt: -1 }}
            filter={{ livemode: false, page: page._id }}
            refreshKey={refreshKey}
          />
        )}
      </CardContent>
    </div>
  );
}

function TestSubscriptions({ page }) {
  const [refreshKey, setRefreshKey] = useState(0);

  // listen to postmessage checkout event
  React.useEffect(() => {
    const handleCheckoutEvent = event => {
      if (event.data?.type === 'checkoutpage.event') {
        if (event.data.payload.event === 'checkout') {
          setRefreshKey(refreshKey + 1);
        }
      }
    };

    window.addEventListener('message', handleCheckoutEvent);

    return () => {
      window.removeEventListener('message', handleCheckoutEvent);
    };
  }, [refreshKey]);

  return (
    <div className="w-full">
      <div className="my-8">
        <div className="border border-gray-100 rounded">
          <TestCardItem label="Test card number" value="4242 4242 4242 4242" />

          <TestCardItem label="Expiry date" value="12/28" />

          <TestCardItem label="CVC" value="123" isLast />
        </div>
      </div>

      <h1 className="text-xl font-semibold">Subscriptions</h1>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header">
          <CardListItemElement size="xl">Customer</CardListItemElement>
          <CardListItemElement size="large">Amount</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
        </CardListItem>

        {page.type === 'checkout' && (
          <PaymentsSubscriptionsList
            options={{
              hidePaymentLink: true,
              hideStatus: true,
              appearance: 'compact',
            }}
            sort={{ createdAt: -1 }}
            filter={{ livemode: false, page: page._id }}
            refreshKey={refreshKey}
          />
        )}
      </CardContent>
    </div>
  );
}

export default PageTest;
